import { apiClient } from "./apiClient";

export type CronometroStatus = "parado" | "rodando" | "pausado" | "concluido";
export type AcaoCronometro =
  | "iniciar"
  | "pausar"
  | "retomar"
  | "concluir"
  | "reabrir";

export interface SugestaoBorda {
  bordaChave: string;
  bordaNome: string;
  tamanho: string;
  tamanhoChave: string;
  quantidadeBase: number;
  quantidadeSugerida: number;
}

export interface ProducaoBordaItem {
  id: number;
  bordaChave: string;
  bordaNome: string;
  tamanho: string;
  tamanhoChave: string;
  quantidadeBase: number;
  quantidadeSugerida: number;
  quantidadePlanejada: number;
  consumido: number;
  restante: number;
  excedente: number;
  cronometro: {
    status: CronometroStatus;
    iniciadoEm: string | null;
    rodandoDesde: string | null;
    tempoAcumuladoMs: number;
    concluidoEm: string | null;
  };
}

export interface ProducaoBorda {
  id: number;
  status: "em_producao" | "finalizada";
  data: string;
  dataBase: string;
  percentual: number;
  confirmadoPorNome: string | null;
  confirmadoEm: string;
  finalizadoEm: string | null;
  responsavelNome: string | null;
  imagemAssinatura: string | null;
  observacao: string | null;
  itens: ProducaoBordaItem[];
  foraDoPlano: {
    bordaChave: string;
    bordaNome: string;
    tamanho: string;
    tamanhoChave: string;
    consumido: number;
  }[];
}

export interface PainelBordeiroDia {
  lojaId: number;
  data: string;
  dataBase: string;
  hoje: string;
  diaSemana: string;
  percentual: number;
  podeGerarProducao: boolean;
  podeAlterarQuadro: boolean;
  sugestao: SugestaoBorda[];
  producao: ProducaoBorda | null;
  serverNow: string;
}

export interface PainelBordeiroSemana {
  lojaId: number;
  hoje: string;
  percentual: number;
  inicio: string;
  fim: string;
  dias: {
    data: string;
    dataBase: string;
    diaSemana: string;
    totalBase: number;
    totalSugerida: number;
    totalPlanejada: number | null;
    producaoStatus: ProducaoBorda["status"] | null;
  }[];
  bordas: {
    bordaChave: string;
    bordaNome: string;
    dias: Record<
      string,
      { base: number; sugerida: number; planejada: number | null }
    >;
  }[];
}

export interface ProducaoBordasHoje {
  lojaId: number;
  data: string;
  diaSemana: string;
  producao: ProducaoBorda | null;
  serverNow: string;
}

/** Agrupa linhas planas (borda+tamanho) em um card por borda. */
export function agruparPorBorda<
  T extends { bordaChave: string; bordaNome: string },
>(linhas: T[]): { bordaChave: string; bordaNome: string; tamanhos: T[] }[] {
  const mapa = new Map<
    string,
    { bordaChave: string; bordaNome: string; tamanhos: T[] }
  >();
  for (const linha of linhas) {
    let g = mapa.get(linha.bordaChave);
    if (!g) {
      g = {
        bordaChave: linha.bordaChave,
        bordaNome: linha.bordaNome,
        tamanhos: [],
      };
      mapa.set(linha.bordaChave, g);
    }
    g.tamanhos.push(linha);
  }
  return Array.from(mapa.values());
}

type Filtro = { data?: string; lojaId?: number | null; percentual?: number };

function toQuery(f: Filtro): string {
  const p = new URLSearchParams();
  if (f.data) p.set("data", f.data);
  if (f.lojaId != null) p.set("lojaId", String(f.lojaId));
  if (f.percentual != null) p.set("percentual", String(f.percentual));
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

export const painelBordeiroService = {
  getDia: (f: Filtro) =>
    apiClient.get<PainelBordeiroDia>(`/painel-bordeiro/dia${toQuery(f)}`, {
      cache: "no-store",
    }),

  getSemana: (f: Filtro) =>
    apiClient.get<PainelBordeiroSemana>(
      `/painel-bordeiro/semana${toQuery(f)}`,
      { cache: "no-store" }
    ),

  getTvDia: (f: Filtro) =>
    apiClient.get<PainelBordeiroDia>(`/painel-bordeiro/tv/dia${toQuery(f)}`, {
      cache: "no-store",
    }),

  getTvSemana: (f: Filtro) =>
    apiClient.get<PainelBordeiroSemana>(
      `/painel-bordeiro/tv/semana${toQuery(f)}`,
      { cache: "no-store" }
    ),

  getProducaoBordeiro: (lojaId?: number | null) =>
    apiClient.get<ProducaoBordasHoje>(
      `/painel-bordeiro/bordeiro/producao${toQuery({ lojaId })}`,
      { cache: "no-store" }
    ),

  salvarProducao: (body: {
    data: string;
    lojaId?: number | null;
    percentual: number;
    itens: {
      bordaNome: string;
      tamanho: string;
      quantidadeBase: number;
      quantidadeSugerida: number;
      quantidadePlanejada: number;
    }[];
  }) =>
    apiClient.post<PainelBordeiroDia>("/painel-bordeiro/producao", {
      ...body,
      lojaId: body.lojaId ?? undefined,
    }),

  cronometro: (itemId: number, acao: AcaoCronometro) =>
    apiClient.post<ProducaoBordasHoje>(
      `/painel-bordeiro/producao/itens/${itemId}/cronometro`,
      { acao }
    ),

  finalizar: (
    producaoId: number,
    body: { responsavelNome: string; assinatura: string; observacao?: string }
  ) =>
    apiClient.post<ProducaoBordasHoje>(
      `/painel-bordeiro/producao/${producaoId}/finalizar`,
      body
    ),
};
